import { Injectable } from '@angular/core';
import { ServiceCreditItem, ServiceCreditTypesEnum, User } from '../models/user.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { AccountAttributesEnum } from '../../shared/enums';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private readonly userSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);

    setUserData(user: User): void {
        this.userSubject.next(user);
    }

    haUserData(): boolean {
      return !!this.userSubject.getValue();
    }

    getUserData(): Observable<User> {
        return this.userSubject.asObservable().pipe(shareReplay(1));
    }

    clearUserData(): void {
        this.userSubject.next(null);
    }

    hasAccess(attributes: AccountAttributesEnum | AccountAttributesEnum[], every?: boolean): boolean {
        const userData: User = this.userSubject.getValue();
        if (!Array.isArray(attributes)) {
            return userData?.account_attributes?.includes(attributes);
        }

        if (every) {
            return attributes.every((attr: AccountAttributesEnum) => userData?.account_attributes?.includes(attr));
        }

        return attributes.some((attr: AccountAttributesEnum) => userData?.account_attributes?.includes(attr));
    }

    hasAccessAsync(attributes: AccountAttributesEnum | AccountAttributesEnum[], every?: boolean): Observable<boolean> {
        return this.userSubject.asObservable().pipe(
            filter(Boolean),
            map((userData: User) => {
                if (!Array.isArray(attributes)) {
                    return userData?.account_attributes?.includes(attributes);
                }

                if (every) {
                    return attributes.every((attr: AccountAttributesEnum) => userData?.account_attributes?.includes(attr));
                }

                return attributes.some((attr: AccountAttributesEnum) => userData?.account_attributes?.includes(attr));
            }),
        )
    }

    isAdmin(): boolean {
        const userData: User = this.userSubject.getValue();
        return userData?.is_admin;
    }

    isOpsAdmin(): boolean {
        const userData: User = this.userSubject.getValue();
        return userData?.is_ops_admin;
    }

    getUserAccountType(): number {
        const userData: User = this.userSubject.getValue();
        return userData.account_type;
    }

    getUserAccountId(): number {
        const userData: User = this.userSubject.getValue();
        return userData.account_id;
    }

    getUserAccountUUID(): string {
        const userData: User = this.userSubject.getValue();
        return userData.uuid;
    }

    listenToUserChangesByKey<T>(key: keyof User): Observable<T> {
        return this.userSubject.asObservable().pipe(
            shareReplay(1),
            filter(Boolean),
            map((user: User) => user[key]),
            distinctUntilChanged(),
        )
    }

    listenToUserMetaChanges(key: string): Observable<any> {
        return this.userSubject.asObservable().pipe(
            shareReplay(1),
            filter(Boolean),
            map((user: User) => user.user_meta[key]),
            distinctUntilChanged(),
        )
    }

    getServiceCreditByType(type: ServiceCreditTypesEnum): Observable<ServiceCreditItem> {
        return this.userSubject.asObservable().pipe(
            filter(Boolean),
            map((user: User) => user.service_credits?.find(({ credit_type }: ServiceCreditItem) => credit_type === type)),
        );
    }

    // TODO: temporary added just for Visitor Intel, in future consider universal function for all service credit types
    getRodRemainingServiceCredits(): Observable<number> {
        return this.getServiceCreditByType(ServiceCreditTypesEnum.ROD).pipe(
            map((credit: ServiceCreditItem) => {
                const userData: User = this.userSubject.getValue();

                return credit?.credits_remaining + (userData.has_licence_credits ? credit?.license_credit_limit - credit?.license_credits_used : 0) || 0;
            }),
        )
    }

    hasServiceCredit(types: ServiceCreditTypesEnum | ServiceCreditTypesEnum[]): Observable<boolean> {
        types = Array.isArray(types) ? types : [types]
        return this.userSubject.asObservable().pipe(
            filter(Boolean),
            map((userData: User) => userData?.service_credits?.some(({ credit_type }: ServiceCreditItem) =>  types.includes(credit_type)))
        )
    }
}
